import React, { useState, useEffect } from 'react';
import { X, Mail, Lock, User, CheckCircle, Clock, Shield } from 'lucide-react';
import { useModalScrollLock } from '../hooks/useModalScrollLock';
import toast from 'react-hot-toast';
import { readSession } from '../contexts/AuthContext';
import { generateCSRFToken, getOrCreateDeviceId } from '../utils/security';

const magicLinkDeduplicator = {
  _requests: {},
  canSendRequest(email) {
    const last = this._requests[email];
    return !last || Date.now() - last.ts > 30000;
  },
  getRemainingCooldown(email) {
    const last = this._requests[email];
    return last ? Math.max(0, 30000 - (Date.now() - last.ts)) : 0;
  },
  recordRequest(email, requestId) {
    this._requests[email] = { ts: Date.now(), requestId };
  },
};


const AUTH_COPY = {
  'insight-library': {
    title:   'Get Free Access',
    icon:    'download',
    message: 'Sign in with your email to download this resource instantly. No password needed — just a magic link.',
  },
  'resume': {
    title:   'Build Your Resume',
    icon:    'user',
    message: 'Sign in to save your resume progress and download your ATS-optimised PDF.',
  },
  'contest': {
    title:   'Join the Contest',
    icon:    'trophy',
    message: 'Sign in to submit your entry and compete with the QA community.',
  },
  'boards': {
    title:   'Access My Boards',
    icon:    'bookmark',
    message: 'Sign in to view and manage your saved boards.',
  },
  'comment': {
    title:   'Join the Discussion',
    icon:    'user',
    message: "To continue, please verify your email address. We'll send you a magic link for instant access.",
  },
};

const AuthModal = ({ isOpen, onClose, onSuccess, action = 'comment' }) => {
  const copy = AUTH_COPY[action] || AUTH_COPY['comment'];
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('email'); // 'email', 'verify', 'rate_limited'
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [requestId, setRequestId] = useState(null);


  // Prevent background scrolling
  useModalScrollLock(isOpen);

  // Check authentication status on mount
  useEffect(() => {
    if (!isOpen) return;
    // Use the centralised session reader — only considers verified sessions
    const session = readSession();
    if (session?.email) {
      onClose();
      if (onSuccess) onSuccess();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard accessibility: Close on Escape key press
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const sanitizedEmail = email.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '').trim().toLowerCase();
    
    if (!sanitizedEmail) {
      toast.error('Please enter your email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sanitizedEmail) || sanitizedEmail.length > 254) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check for concurrent requests
    if (!magicLinkDeduplicator.canSendRequest(sanitizedEmail)) {
      const remainingTime = Math.ceil(magicLinkDeduplicator.getRemainingCooldown(sanitizedEmail) / 1000);
      toast.error(`Please wait ${remainingTime} seconds before requesting another magic link`);
      return;
    }

    setLoading(true);
    const currentRequestId = Math.random().toString(36).substring(2, 15);
    setRequestId(currentRequestId);

    try {
      // Get CSRF token for security
      const csrfToken = generateCSRFToken();
      
      const response = await fetch('/api/auth?action=send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-ID': currentRequestId,
          'X-Client-Version': '1.0.0',
          'x-csrf-token': csrfToken,
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          email: sanitizedEmail,
          deviceId: getOrCreateDeviceId(),
          requestId: currentRequestId,
          timestamp: Date.now()
        })
      });

      let result;
      try {
        const responseText = await response.text();
        result = responseText ? JSON.parse(responseText) : { success: false, error: 'Empty response' };
      } catch (jsonError) {
        console.error('JSON parse error:', jsonError);
        result = { success: false, error: 'Invalid response format' };
      }

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 429) {
          setRateLimitInfo({ 
            message: result.error || 'Rate limit exceeded',
            retryAfter: result.retryAfter 
          });
          setStep('rate_limited');
          return;
        }
        
        throw new Error(result.error || `Server error: ${response.status}`);
      }
      
      // Ensure we have a success response
      if (!result.success) {
        throw new Error(result.error || 'Authentication failed');
      }

      // Handle successful email sending - accept any success response
      if (result.success) {
        // Record the request to prevent duplicates
        magicLinkDeduplicator.recordRequest(sanitizedEmail, currentRequestId);
        
        setStep('verify');
        toast.success(`✅ Verification email sent to ${sanitizedEmail}`);
        
        // Store email for verification tracking
        sessionStorage.setItem('pending_auth_email', sanitizedEmail);
      } else {
        throw new Error(result.error || 'Failed to send verification email');
      }
      
    } catch (error) {
      
      
      let errorMessage = 'Failed to send magic link';
      
      if (error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to authentication service. Please check your internet connection and try again.';
      } else if (error.message.includes('Rate limit')) {
        setRateLimitInfo({ message: error.message });
        setStep('rate_limited');
        return;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDevAutoVerify = (email) => {
    // Dev-only: creates a mock verified session that includes a token field
    // so getSessionToken() returns a non-null value for payment flows.
    // The token is a fake HMAC-shaped string — it will fail server validation
    // in production (TOKEN_SECRET won't match), which is correct behaviour.
    const mockUser = {
      id: `dev-${Date.now()}`,
      email: email,
      email_confirmed_at: new Date().toISOString(),
      user_metadata: { full_name: email.split('@')[0], role: 'user' },
      role: 'user',
    };

    localStorage.setItem('dev_auth_user', JSON.stringify(mockUser));

    // Build a session that matches the shape AuthContext.readSession() expects
    const authSession = {
      email:         email,
      verified:      true,
      role:          'user',
      deviceId:      getOrCreateDeviceId(),
      loginTime:     Date.now(),
      expiresAt:     Date.now() + (30 * 24 * 60 * 60 * 1000),
      lastActivity:  Date.now(),
      user:          mockUser,
      schemaVersion: 3, // must match CURRENT_SCHEMA_VERSION in AuthContext
      // Placeholder token — dev only, will fail real HMAC validation
      token:         `devtoken.${Date.now()}.devhash0000000`,
    };
    localStorage.setItem('auth_session', JSON.stringify(authSession));

    window.dispatchEvent(new CustomEvent('auth-state-change', {
      detail: { user: mockUser, session: { user: mockUser } },
    }));

    onClose();
    if (onSuccess) onSuccess();
  };

  // Action-specific auth message
  const standardAuthMessage = copy.message;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[999999] p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full max-h-[92dvh] sm:max-h-[calc(90vh-80px)] overflow-y-auto pb-safe">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 id="auth-modal-title" className="text-xl font-bold text-gray-900">
            {step === 'email' ? copy.title : 'Check Your Email'}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close authentication modal"
            className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-1"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {step === 'rate_limited' ? (
            <>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Rate Limit Reached
                </h3>
                <p className="text-gray-600 mb-6">
                  {rateLimitInfo?.message || 'You\'ve reached the maximum number of magic link requests.'}
                </p>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div className="text-sm text-orange-800">
                      <p className="font-medium mb-1">Security Protection Active</p>
                      <ul className="space-y-1 text-xs">
                        <li>• Maximum 5 requests per hour per device</li>
                        <li>• This prevents unauthorized access attempts</li>
                        <li>• Your limit will reset automatically</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setStep('email');
                      setRateLimitInfo(null);
                    }}
                    className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 font-medium"
                  >
                    Try Different Email
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </>
          ) : step === 'email' ? (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-white" />
                </div>
                <p className="text-gray-600">
                  {standardAuthMessage}
                </p>
              </div>

              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500 bg-white"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Sending magic link...
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      Send Magic Link
                    </>
                  )}
                </button>
              </form>



              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500">
                  By continuing, you agree to our Terms of Service and Privacy Policy. 
                  Magic links are sent securely via email.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Check Your Email
                </h3>
                <p className="text-gray-600 mb-6">
                  We've sent a secure verification link to <strong>{email}</strong>. 
                  Click the link in your email to complete authentication.
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Next Steps:</p>
                      <ul className="space-y-1 text-xs">
                        <li>• Check your email inbox (and spam folder)</li>
                        <li>• Click the "Verify Account" button in the email</li>
                        <li>• You'll be automatically signed in with proper role</li>
                        {requestId && <li>• Request ID: {requestId}</li>}
                        <li>• Email sent securely</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep('email')}
                    className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 font-medium"
                  >
                    Send to Different Email
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  );
};

export default AuthModal;